import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Tab, Row, Col, Card, ListGroup, Form, Button } from 'react-bootstrap';
import DashboardLayout from './DashboardLayout';
import { fetchCategoryActivities, addActivityToFavorites, removeActivityFromFavorites } from '../../api/api';
import { difficultiesMap } from './constants/constants';
import { Heart, HeartFill } from 'react-bootstrap-icons';
import AuthenticatedImage from '../../components/AuthenticatedImage';
import CustomPagination from '../../components/CustomPagination';

const CategoryActivities = () => {
  const { id } = useParams();

  const [filtering, setFiltering] = useState(false);
  const [pageMessage, setPageMessage] = useState("Buscando atividades...");

  const [activities, setActivities] = useState([]);
  const [categoryTitle, setCategoryTitle] = useState('');
  const [selectedDifficulty, setSelectedDifficulty] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPage, setTotalPage] = useState(1);

  const itemsPerPage = 8;
  const navigate = useNavigate();
  let searchTimeout;

  const [searchParams] = useSearchParams();
  const favoritesOnly = searchParams.get('favorites_only') === 'true';

  const fetchDataForPage = async (page, term, difficulty) => {
    try {
      const response = await fetchCategoryActivities(id, page, itemsPerPage, term, difficulty, favoritesOnly);
      setCategoryTitle(response.category_name);
      setTotalPage(response.total_count);
      setActivities(response.activities);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  useEffect(() => {
    fetchDataForPage(currentPage, searchTerm, selectedDifficulty);
    if (activities.length === 0) {
      setPageMessage("Nenhuma atividade encontrada");
    }
  }, [id]);

  useEffect(() => {
    if (searchTerm || selectedDifficulty) {
      setFiltering(true);
    } else {
      setFiltering(false);
    }

    searchTimeout = setTimeout(() => {
      setCurrentPage(1);
      fetchDataForPage(1, searchTerm, selectedDifficulty);
    }, 500);
    return () => clearTimeout(searchTimeout);
  }, [searchTerm, selectedDifficulty]);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleDifficultyChange = (e) => {
    setSelectedDifficulty(e.target.value);
  };

  const handleEdit = (exercise) => {
    if (exercise) {
      navigate('/dashboard/activity/edit/' + exercise.uuid);
    }
  };

  const paginate = async (pageNumber) => {
    await setCurrentPage(pageNumber);
    fetchDataForPage(pageNumber, searchTerm, selectedDifficulty);
  }

  const handlePrint = (e, url) => {
    e.stopPropagation();
    if (url) {
      const encodedUrl = encodeURIComponent(url);
      navigate(`/print?url=${encodedUrl}`);
    }
  };

  const toggleFavorite = async (e, activity) => {
    e.stopPropagation();
    try {
      if (activity.is_favorited) {
        await removeActivityFromFavorites(activity.uuid);
      } else {
        await addActivityToFavorites(activity.uuid);
      }

      // Update local state
      setActivities(prevActivities => prevActivities.map(act =>
        act.uuid === activity.uuid ? { ...act, is_favorited: !act.is_favorited } : act
      ));
    } catch (error) {
      console.error("Error toggling favorite:", error);
    }
  };

  return (
    <DashboardLayout title={"Categoria: " + categoryTitle}>
      {filtering || activities.length > 0 ? (
        <div>
          <Tab.Container id="level-tabs">
            <Row>
              <Col sm={9}>
                <Row>
                  <Col sm={10}>
                    <Form.Group className="mb-3">
                      <Form.Control
                        type="text"
                        placeholder="Filtrar atividades..."
                        value={searchTerm}
                        onChange={handleSearchChange}
                      />
                    </Form.Group>
                  </Col>
                  <Col sm={2}>
                    <Form.Group className="mb-3">
                      <Form.Select value={selectedDifficulty} onChange={handleDifficultyChange}>
                        <option value="">Dificuldades</option>
                        {Object.entries(difficultiesMap).map(([key, value]) => (
                          <option key={key} value={key}>{value}</option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>
                <Tab.Content>
                  <Row>
                    {activities.length > 0 ? (
                      activities.map(activity => (
                        <Col sm={6} md={6} lg={3} className="mb-3" key={activity.uuid}>
                          <Card
                            className="shadow-sm h-100"
                            style={{ width: '100%', cursor: 'pointer' }}
                            onClick={() => handleEdit(activity)}
                          >
                            <AuthenticatedImage variant="top" url={activity.image_url} imageWidth={150} style={{ maxHeight: '150px', width: 'auto', maxWidth: '100%', objectFit: 'contain', margin: '0 auto' }} />
                            <Card.Body className="flex-grow-1">
                              <Card.Title>
                                {activity.title && activity.title.length > 19
                                  ? `${activity.title.substring(0, 16)}...`
                                  : activity.title}
                              </Card.Title>
                              <Card.Text>
                                {activity.description && activity.description.length > 22
                                  ? `${activity.description.substring(0, 19)}...`
                                  : activity.description}
                              </Card.Text>
                              <div className="d-flex gap-2 mt-auto w-100">
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  className="flex-grow-1"
                                  onClick={(e) => handlePrint(e, activity.url)}
                                >
                                  Imprimir
                                </Button>
                                <Button
                                  variant="outline-secondary"
                                  size="sm"
                                  onClick={(e) => toggleFavorite(e, activity)}
                                  title={activity.is_favorited ? "Remover dos favoritos" : "Adicionar aos favoritos"}
                                >
                                  {activity.is_favorited ? <HeartFill color="red" /> : <Heart />}
                                </Button>
                              </div>
                            </Card.Body>
                            <ListGroup className="list-group-flush">
                              <ListGroup.Item>{difficultiesMap[activity.difficulty]}</ListGroup.Item>
                            </ListGroup>
                          </Card>
                        </Col>
                      ))
                    ) : (
                      <Col>
                        <p>{pageMessage}</p>
                      </Col>
                    )}
                  </Row>

                  <br />

                  <CustomPagination
                    currentPage={currentPage}
                    totalPage={totalPage}
                    itemsPerPage={itemsPerPage}
                    paginate={paginate}
                  />
                </Tab.Content>
              </Col>
            </Row>
          </Tab.Container>
        </div>
      ) : (
        <p>{pageMessage}</p>
      )}
    </DashboardLayout>
  );
};

export default CategoryActivities;
